interface TierInfo {
  tier: string;
  name: string;
  price: number;
  tokensMillions: number;
  refillsAllowed: boolean;
}

interface PriceTableTieredAIHubProps {
  selectedTier?: string;
  showDescription?: boolean;
}

const tierData: TierInfo[] = [
  {
    tier: "trial",
    name: "Trial",
    price: 25,
    tokensMillions: 5,
    refillsAllowed: false,
  },
  {
    tier: "mid",
    name: "Professional",
    price: 1500,
    tokensMillions: 100,
    refillsAllowed: true,
  },
  {
    tier: "high",
    name: "Enterprise",
    price: 3000,
    tokensMillions: 250,
    refillsAllowed: true,
  },
];

const PriceTableTieredAIHub = ({
  selectedTier,
  showDescription = false,
}: PriceTableTieredAIHubProps) => {
  const selectedTierData = tierData.find((t) => t.tier === selectedTier);

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      {showDescription && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            AI Hub Subscription
          </h3>
          <p className="text-sm text-gray-600">
            Tiered pricing with included tokens and optional auto-refill
          </p>
        </div>
      )}

      {selectedTierData ? (
        // Show selected tier details
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500">Plan</span>
            <span className="text-sm font-semibold text-indigo-600">
              {selectedTierData.name}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500">
              Monthly Price
            </span>
            <span className="text-sm font-semibold">
              ${selectedTierData.price.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500">
              Included Tokens
            </span>
            <span className="text-sm font-semibold">
              {selectedTierData.tokensMillions}M
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500">
              Token Rate
            </span>
            <span className="text-sm font-semibold">$1.50 / 1M tokens</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500">
              Auto-Refill
            </span>
            <span
              className={`text-sm font-semibold ${
                selectedTierData.refillsAllowed
                  ? "text-green-600"
                  : "text-amber-600"
              }`}
            >
              {selectedTierData.refillsAllowed ? "Enabled" : "Not Available"}
            </span>
          </div>

          {!selectedTierData.refillsAllowed && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              Trial plans do not include refills. Upgrade to Professional or
              Enterprise to enable auto-refill.
            </div>
          )}
        </div>
      ) : (
        // Show comparison table
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr>
                <th className="py-2 text-left text-xs font-medium text-gray-500">
                  Plan
                </th>
                <th className="py-2 text-right text-xs font-medium text-gray-500">
                  Price
                </th>
                <th className="py-2 text-right text-xs font-medium text-gray-500">
                  Tokens
                </th>
                <th className="py-2 text-center text-xs font-medium text-gray-500">
                  Refills
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tierData.map((tier) => (
                <tr key={tier.tier}>
                  <td className="py-2 font-medium text-gray-900">
                    {tier.name}
                  </td>
                  <td className="py-2 text-right text-gray-600">
                    ${tier.price.toLocaleString()}
                  </td>
                  <td className="py-2 text-right text-gray-600">
                    {tier.tokensMillions}M
                  </td>
                  <td className="py-2 text-center">
                    {tier.refillsAllowed ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-red-500">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-xs text-gray-500">
            All plans: $1.50 per million tokens
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceTableTieredAIHub;
